import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { AccountStep, RoleStep, LocationStep, HeadshotStep, TopicsStep, BehaviouralStep } from './SetupSteps';
import { BEHAVIOURAL_QUESTIONS } from '@/utils/behavioural';

const need = (v, keys) => keys.every(k => String(v[k] || '').trim());

const STEPS = [
  { title: 'Your account', Comp: AccountStep,
    check: (v, { mode, terms }) => !need(v, mode === 'signup' ? ['name', 'email', 'password'] : ['name']) ? 'Please fill in every field'
      : mode === 'signup' && !terms.agreed ? 'Please accept the Terms & Conditions' : null },
  { title: 'Your role', Comp: RoleStep,
    check: v => need(v, ['role', 'organisation', 'background', 'sector']) ? null : 'Please fill in your job title, company, stakeholder type and sector' },
  { title: 'Where you are based', Comp: LocationStep,
    check: v => need(v, ['country', 'location', 'region']) ? null : 'Please choose your country, city and region' },
  { title: 'Your headshot', Comp: HeadshotStep,
    check: (v, { photoUrl }) => photoUrl ? null : 'Please add a headshot' },
  { title: 'Forum topics of interest', Comp: TopicsStep,
    check: v => (v.forum_topics_of_interest || []).length ? null : 'Please pick at least one topic' },
  { title: 'Your behavioural profile', Comp: BehaviouralStep,
    check: v => need(v, BEHAVIOURAL_QUESTIONS.map(q => q.key)) ? null : 'Please answer all four questions' },
];

export default function ProfileSetupWizard({ mode = 'signup', initial = {}, initialPhotoUrl = null, terms, onSubmit, submitLabel = 'Create Account' }) {
  const [step, setStep] = useState(0);
  const [v, setV] = useState(initial);
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(initialPhotoUrl);
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setV(p => ({ ...p, [k]: val }));
  const { title, Comp } = STEPS[step];
  const last = step === STEPS.length - 1;

  const next = async () => {
    const err = STEPS[step].check(v, { mode, terms, photoUrl });
    if (err) { toast.error(err); return; }
    if (!last) { setStep(step + 1); return; }
    setBusy(true);
    try { await onSubmit(v, photo); } finally { setBusy(false); }
  };

  return (
    <div data-testid="profile-setup-wizard">
      <div className="flex items-center gap-1.5 mb-4" data-testid="setup-progress">
        {STEPS.map((s, i) => <div key={s.title} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-brand-navy' : 'bg-slate-200'}`} />)}
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Step {step + 1} of {STEPS.length}</p>
      <h3 className="text-lg font-bold text-slate-800 mb-4" data-testid="setup-step-title">{title}</h3>
      <Comp v={v} set={set} mode={mode} terms={terms} photoUrl={photoUrl}
        onPhoto={blob => { setPhoto(blob); setPhotoUrl(URL.createObjectURL(blob)); }} />
      <div className="flex gap-3 mt-6">
        {step > 0 && (
          <Button type="button" variant="outline" onClick={() => setStep(step - 1)} className="h-11" data-testid="setup-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        )}
        <Button type="button" onClick={next} disabled={busy} data-testid={last ? 'setup-submit-btn' : 'setup-next-btn'}
          className="flex-1 h-11 bg-gradient-to-r from-brand-navy to-brand-sage text-white">
          {busy ? 'Saving…' : last ? submitLabel : <>Next <ArrowRight className="h-4 w-4 ml-1" /></>}
        </Button>
      </div>
    </div>
  );
}
